import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
  ConnectionStatus,
  DeviceState,
  DeviceStateStore,
} from "@production-hub/shared";

export type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
  ConnectionStatus,
  DeviceState,
  DeviceStateStore,
};

export interface ConnectionWithStatus extends ConnectionConfig {
  status: ConnectionStatus;
}

interface BridgeStatusResponse {
  status: "ok" | "error";
  timestamp: string;
  connections: {
    total: number;
    enabled: number;
    connected: number;
    details: Array<{
      id: string;
      name: string;
      type: string;
      status: ConnectionStatus;
    }>;
  };
}

/**
 * Check if bridge is reachable
 */
export const useBridgeStatus = (bridgeUrl: string | undefined | null) => {
  return useQuery({
    queryKey: ["bridge-status", bridgeUrl],
    queryFn: async (): Promise<BridgeStatusResponse> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/status`);
      if (!response.ok) {
        throw new Error(`Bridge returned ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000, // Poll every 5 seconds
    retry: false,
  });
};

/**
 * Fetch all connections from a bridge
 */
export const useBridgeConnections = (bridgeUrl: string | undefined | null) => {
  return useQuery({
    queryKey: ["bridge-connections", bridgeUrl],
    queryFn: async (): Promise<ConnectionWithStatus[]> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections`);
      if (!response.ok) {
        throw new Error(`Failed to fetch connections: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000, // Poll for status updates
  });
};

/**
 * Fetch a single connection from a bridge
 */
export const useBridgeConnection = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
) => {
  return useQuery({
    queryKey: ["bridge-connections", bridgeUrl, connectionId],
    queryFn: async (): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");
      if (!connectionId) throw new Error("No connection ID");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch connection: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl && !!connectionId,
    refetchInterval: 2000, // Poll faster for single connection
  });
};

/**
 * Create a new connection on a bridge
 */
export const useCreateBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      connection: ConnectionInsert,
    ): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connection),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to create connection: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection created");
    },
    onError: (error) => {
      toast.error("Failed to create connection: " + error.message);
    },
  });
};

/**
 * Update a connection on a bridge
 */
export const useUpdateBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      connection,
    }: {
      id: string;
      connection: ConnectionUpdate;
    }): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connection),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to update connection: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection updated");
    },
    onError: (error) => {
      toast.error("Failed to update connection: " + error.message);
    },
  });
};

/**
 * Delete a connection from a bridge
 */
export const useDeleteBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections/${id}`, {
        method: "DELETE",
      });

      if (!response.ok && response.status !== 204) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to delete connection: ${response.status}`,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete connection: " + error.message);
    },
  });
};

/**
 * Connect to a device
 */
export const useConnectDevice = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${id}/connect`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `Failed to connect: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connected to device");
    },
    onError: (error) => {
      toast.error("Failed to connect: " + error.message);
    },
  });
};

/**
 * Disconnect from a device
 */
export const useDisconnectDevice = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${id}/disconnect`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to disconnect: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Disconnected from device");
    },
    onError: (error) => {
      toast.error("Failed to disconnect: " + error.message);
    },
  });
};

/**
 * Get device state
 */
export const useDeviceState = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
) => {
  return useQuery({
    queryKey: ["device-state", bridgeUrl, connectionId],
    queryFn: async (): Promise<DeviceState> => {
      if (!bridgeUrl) throw new Error("No bridge URL");
      if (!connectionId) throw new Error("No connection ID");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/state`,
      );
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to get state: ${response.status}`,
        );
      }

      return response.json();
    },
    enabled: !!bridgeUrl && !!connectionId,
    refetchInterval: 1000, // Poll frequently for live state
    retry: false,
  });
};

/**
 * Send a command to a device
 */
export interface DeviceCommand {
  action: string;
  [key: string]: unknown;
}

export const useSendCommand = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      command,
    }: {
      connectionId: string;
      command: DeviceCommand;
    }): Promise<{ success: boolean }> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/command`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(command),
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `Command failed: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate device state to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["device-state", bridgeUrl, variables.connectionId],
      });
    },
    onError: (error) => {
      toast.error("Command failed: " + error.message);
    },
  });
};

/**
 * Get device state as OSC-style store
 */
export const useBridgeDeviceStore = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
) => {
  return useQuery({
    queryKey: ["device-store", bridgeUrl, connectionId],
    queryFn: async (): Promise<DeviceStateStore> => {
      if (!bridgeUrl) throw new Error("No bridge URL");
      if (!connectionId) throw new Error("No connection ID");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/store`,
      );
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to get store: ${response.status}`,
        );
      }

      return response.json();
    },
    enabled: !!bridgeUrl && !!connectionId,
    refetchInterval: 1000,
    retry: false,
  });
};

/**
 * Update a single value in the device state store
 */
export const useUpdateBridgeDeviceStore = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      path,
      value,
    }: {
      connectionId: string;
      path: string;
      value: string | number | boolean;
    }): Promise<{ success: boolean }> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/store`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path, value }),
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Store update failed: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["device-store", bridgeUrl, variables.connectionId],
      });
    },
    onError: (error) => {
      toast.error("Store update failed: " + error.message);
    },
  });
};
